import { z } from 'zod';
import { idSchema } from './common.js';
import { cuisineSchema } from './recipes.js';
import { dateOnlySchema, mealPlanDtoSchema, mealSlotSchema, timeOfDaySchema } from './plans.js';
import { allergenSchema, placeKindSchema, planAttendeesInputSchema, plannerVibeSchema, } from './profiles.js';
/**
 * Week planner (D29) and quick planner v2 (D33): propose a week of meals that
 * fits the household's routine, people and budget. Every v2 field is
 * optional; leaving them all out reproduces the D29 behaviour.
 */
export const PLANNER_MAX_DAYS = 14;
/** Alternatives returned per proposed meal (D33 "Swap" list). */
export const PLANNER_MAX_ALTERNATIVES = 5;
/** Default dine-out estimate per attendee when the request gives none (D32; owner may change). */
export const DEFAULT_DINE_OUT_CENTS_PER_PERSON = 1500;
/**
 * Key of a per-meal map: `YYYY-MM-DD|slot`, or a bare `YYYY-MM-DD`, which
 * means the request's `slot` (the D29 form).
 */
export const plannerDayKeySchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}(\|(breakfast|lunch|dinner|snack))?$/, 'YYYY-MM-DD or YYYY-MM-DD|slot');
export const plannerLockSchema = z.object({
    mainRecipeId: idSchema.nullable(),
    sideRecipeIds: z.array(idSchema).max(4).default([]),
});
export const plannerAttendeesSchema = z.object({
    personIds: z.array(idSchema).max(50).default([]),
    /** Guests are counted as adults for servings. */
    guests: z.number().int().min(0).max(30).default(0),
});
export const plannerDineOutSchema = z.object({
    date: dateOnlySchema,
    slot: mealSlotSchema.default('dinner'),
    note: z.string().trim().max(200).optional(),
    /** Default: `DEFAULT_DINE_OUT_CENTS_PER_PERSON` × attendees. */
    estCostCents: z.number().int().min(0).max(10_000_000).optional(),
});
/** POST /api/v1/planner/week */
export const plannerWeekRequestSchema = z.object({
    from: dateOnlySchema,
    days: z.number().int().min(1).max(PLANNER_MAX_DAYS).default(7),
    /** The D29 single slot; also what bare-date keys refer to. */
    slot: mealSlotSchema.default('dinner'),
    /** v2: several slots per day (default `[slot]`). */
    slots: z.array(mealSlotSchema).min(1).max(4).optional(),
    /** v2: only these weekdays (0 = Sunday) of the range are planned (default all). */
    weekdays: z.array(z.number().int().min(0).max(6)).max(7).optional(),
    includeSides: z.boolean().default(false),
    /** key → recipe ids not to propose for that meal ("swap"). */
    exclude: z.record(plannerDayKeySchema, z.array(idSchema).max(100)).default({}),
    /** key → picks to keep as they are. */
    locked: z.record(plannerDayKeySchema, plannerLockSchema).default({}),
    /** Meals the user cleared (keys): nothing is proposed. */
    skip: z
        .array(plannerDayKeySchema)
        .max(PLANNER_MAX_DAYS * 4)
        .default([]),
    /** v2: date → slot → who eats. Default: the meal time's attendees, else everyone. */
    attendees: z
        .record(dateOnlySchema, z.partialRecord(mealSlotSchema, plannerAttendeesSchema))
        .optional(),
    vibe: plannerVibeSchema.optional(),
    /** v2: only these cuisines (untagged recipes pass with a "cuisine unknown" note). */
    cuisines: z.array(cuisineSchema).max(30).optional(),
    /** v2: weekly target in cents; over it, the costliest meals are re-picked. */
    budgetCents: z.number().int().min(0).max(10_000_000).optional(),
    dineOut: z
        .array(plannerDineOutSchema)
        .max(PLANNER_MAX_DAYS * 4)
        .optional(),
    /** v2: date → slot → meal place id: a packed meal (lunch at work/school). */
    packed: z.record(dateOnlySchema, z.partialRecord(mealSlotSchema, idSchema)).optional(),
});
export const plannerRecipeSchema = z.object({
    recipeId: idSchema,
    title: z.string(),
    slug: z.string(),
    photoMediaId: idSchema.nullable(),
    own: z.boolean(),
    /** Step durations, else prep + cook; null = unknown. */
    totalMin: z.number().int().nonnegative().nullable(),
    cuisines: z.array(z.string()),
    courses: z.array(z.string()),
    /** Average over the caller's household people only (D12/D30). */
    householdRating: z.object({ avg: z.number().nullable(), count: z.number().int() }),
    /** v2: allergens detected in the ingredients ("contains: peanut" chips). */
    allergens: z.array(allergenSchema).default([]),
    servings: z.number().int().nullable().optional(),
});
export const plannerReasonSchema = z.object({
    /** preference | try_new | dislike | like | vibe | budget | variety | quick | window | overlap | leftover | expiring | new_perishable | repeat | recency */
    kind: z.string(),
    text: z.string(),
    delta: z.number(),
    ingredientId: idSchema.optional(),
});
/** Cost estimate (D33): observed prices plus seeded category averages for the rest. */
export const plannerCostSchema = z.object({
    totalCents: z.number().int(),
    knownCents: z.number().int(),
    estimatedCents: z.number().int(),
    /** Ingredient ids without an observed price (estimated, or not priced at all). */
    missing: z.array(idSchema),
});
export const plannerWarningSchema = z.object({
    /** dislike | diet_unknown | cuisine_unknown | allergen_unknown */
    kind: z.string(),
    text: z.string(),
    personId: idSchema.optional(),
    ingredientId: idSchema.optional(),
});
export const plannerPickSchema = z.object({
    recipe: plannerRecipeSchema,
    score: z.number(),
    reasons: z.array(plannerReasonSchema),
    /** v2: at the meal's servings. */
    cost: plannerCostSchema.nullable().default(null),
    warnings: z.array(plannerWarningSchema).default([]),
});
export const plannerBusySchema = z.object({
    start: z.iso.datetime(),
    end: z.iso.datetime(),
    label: z.string(),
    /** `calendar` = imported feed event; `routine` = a manual weekly busy block. */
    source: z.enum(['calendar', 'routine']),
});
export const PLANNER_SKIP_KINDS = [
    'already_planned',
    'skipped',
    'busy',
    'no_recipe_fits',
    'no_usual_time',
];
export const PLANNER_DAY_KINDS = ['cook', 'dine_out', 'packed'];
export const plannerDaySchema = z.object({
    date: dateOnlySchema,
    slot: mealSlotSchema,
    /** Local serve time — later than `usualServeAt` when busy time covered it. */
    serveAt: timeOfDaySchema,
    usualServeAt: timeOfDaySchema,
    servePushed: z.boolean(),
    /** "Start cooking" instant for the proposal (or the window start). */
    cookStartAt: z.iso.datetime().nullable(),
    windowStartAt: z.iso.datetime().nullable(),
    /** Minutes available to cook; null = unconstrained. */
    availableMin: z.number().int().nullable(),
    /** "Busy until 5:30 PM · 30 min to cook". */
    busyNote: z.string().nullable(),
    busy: z.array(plannerBusySchema),
    main: plannerPickSchema.nullable(),
    sides: z.array(plannerPickSchema),
    locked: z.boolean(),
    skipped: z.object({ kind: z.enum(PLANNER_SKIP_KINDS), text: z.string() }).nullable(),
    /** The plan already in this slot, when there is one. */
    existingPlan: mealPlanDtoSchema.nullable(),
    // ---- v2 (D33); defaults keep D29 payloads parseable.
    kind: z.enum(PLANNER_DAY_KINDS).default('cook'),
    place: z
        .object({ id: idSchema, name: z.string(), kind: placeKindSchema })
        .nullable()
        .default(null),
    attendees: z
        .object({
        personIds: z.array(idSchema),
        guests: z.number().int(),
        /** True when nobody was named: the whole household. */
        everyone: z.boolean(),
    })
        .nullable()
        .default(null),
    /** Servings the meal is costed (and would be planned) for. */
    servings: z.number().int().nullable().default(null),
    dineOut: z
        .object({ note: z.string().nullable(), estCostCents: z.number().int() })
        .nullable()
        .default(null),
    /** A packed meal that eats the previous dinner's leftovers. */
    leftoversOf: z
        .object({ date: dateOnlySchema, slot: mealSlotSchema, recipeId: idSchema, title: z.string() })
        .nullable()
        .default(null),
    /** The next candidates by score for this meal (≤ 5), for "Swap". */
    alternatives: z.array(plannerPickSchema).default([]),
    /** Meal total (picks or dine-out estimate). */
    cost: plannerCostSchema.nullable().default(null),
});
export const plannerWeekCostSchema = plannerCostSchema.extend({
    budgetCents: z.number().int().nullable(),
    overBudget: z.boolean(),
});
export const plannerWeekResponseSchema = z.object({
    from: dateOnlySchema,
    to: dateOnlySchema,
    slot: mealSlotSchema,
    /** v2: every slot planned, in day order. */
    slots: z.array(mealSlotSchema).default([]),
    timezone: z.string(),
    days: z.array(plannerDaySchema),
    cost: plannerWeekCostSchema.nullable().default(null),
});
export const plannerAcceptDaySchema = z
    .object({
    date: dateOnlySchema,
    slot: mealSlotSchema.default('dinner'),
    serveAt: timeOfDaySchema.nullable().optional(),
    /** Empty only for dine-out and leftovers meals. */
    recipeIds: z.array(idSchema).max(12).default([]),
    // ---- v2
    kind: z.enum(PLANNER_DAY_KINDS).default('cook'),
    placeId: idSchema.nullable().optional(),
    attendees: planAttendeesInputSchema.optional(),
    servings: z.number().int().min(1).max(50).optional(),
    estCostCents: z.number().int().min(0).max(10_000_000).nullable().optional(),
    dineOutNote: z.string().trim().max(200).nullable().optional(),
    /** Packed leftovers: the accepted meal (same request) whose leftovers this eats. */
    leftoversOf: z.object({ date: dateOnlySchema, slot: mealSlotSchema }).optional(),
})
    .refine((d) => d.kind === 'dine_out' || d.leftoversOf || d.recipeIds.length > 0, {
    message: 'A cooked or packed meal needs at least one recipe',
    path: ['recipeIds'],
})
    .refine((d) => d.kind !== 'dine_out' || d.recipeIds.length === 0, {
    message: 'A dine-out meal has no recipes',
    path: ['recipeIds'],
});
/** POST /api/v1/planner/week/accept */
export const plannerAcceptRequestSchema = z.object({
    days: z
        .array(plannerAcceptDaySchema)
        .min(1)
        .max(PLANNER_MAX_DAYS * 4),
    groceryList: z
        .object({
        create: z.boolean(),
        name: z.string().trim().min(1).max(120).optional(),
        storeId: idSchema.nullable().optional(),
    })
        .optional(),
});
export const plannerAcceptResponseSchema = z.object({
    plans: z.array(mealPlanDtoSchema),
    groceryListId: idSchema.nullable(),
});
/**
 * POST /api/v1/planner/week/swap-feedback — the user swapped a proposed
 * meal. `didnt_want` lowers the recipe's ingredients a little for the meal's
 * attendees (learned preferences, D31); `other` records nothing.
 */
export const plannerSwapFeedbackRequestSchema = z.object({
    date: dateOnlySchema,
    slot: mealSlotSchema.default('dinner'),
    recipeId: idSchema,
    reason: z.enum(['didnt_want', 'other']),
    /** Who would have eaten it; default the meal time's attendees, else everyone. */
    personIds: z.array(idSchema).max(50).optional(),
});
export const plannerSwapFeedbackResponseSchema = z.object({
    /** Learned preference rows written or updated. */
    learned: z.number().int().nonnegative(),
});
//# sourceMappingURL=planner.js.map